// Unpublished Work © 2022-2026 Deere & Company.

import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import React from 'react';
import {fetchEffectData} from 'Utils/react-utils';
import {getTodoTemplates} from 'Services/membership-service';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {deleteTodoTemplate} from 'Services/todo-service';
import EditLink from 'Ui/components/common/data-table/edit-link';
import AddButton from 'Ui/components/settings/common/add-button';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {FORBIDDEN} from 'Common/constants/errors';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';

function getDataGridColumns(
    translations,
    fetchTodoTemplate,
    openQuickTodoDialog
) {
    return [
        {
            headerName: translations.ONLINK_QUICK_TODO,
            field: 'title',
            flex: 1,
            renderCell: (params) => {
                return (
                    <EditLink
                        onClick={() =>
                            openQuickTodoDialog({
                                todoTemplate: params.row,
                                getUpdatedTodoTemplates: fetchTodoTemplate
                            })
                        }
                    >
                        {params.value}
                    </EditLink>
                );
            }
        }
    ];
}

function getFilterComponent(
    translations,
    openQuickTodoDialog,
    fetchTodoTemplate
) {
    return (
        <div className='extra-table-options'>
            <AddButton
                className='add-quick-todo-button'
                label={translations.ONLINK_ADD_QUICK_TODO}
                onClick={() => {
                    openQuickTodoDialog({
                        translations,
                        getUpdatedTodoTemplates: fetchTodoTemplate
                    });
                }}
            />
        </div>
    );
}

function QuickTodoSettings(props) {
    const {
        translations,
        openConfirmation,
        closeConfirmation,
        openQuickTodoDialog
    } = props;

    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(() => true);
    const [todoTemplates, setTodoTemplates] = React.useState(() => []);

    async function fetchTodoTemplates(isMounted = () => true) {
        setLoading(true);

        const {todoTemplates} = await getTodoTemplates();

        if (isMounted()) {
            setTodoTemplates(todoTemplates);
            setLoading(false);
        }
    }

    function deleteTodoTemplateById(todoTemplate) {
        openConfirmation({
            message: replaceTranslationNames(
                translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE,
                {
                    '0': translations.ONLINK_QUICK_TODO.toLowerCase()
                }
            ),
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_QUICK_TODO
            }),
            async onContinue() {
                closeConfirmation();
                try {
                    await deleteTodoTemplate(todoTemplate.todoTemplateId);

                    await fetchTodoTemplates();
                } catch (e) {
                    if (e.response.status !== FORBIDDEN) {
                        addToast({
                            message: replaceTranslationNames(
                                translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED,
                                {
                                    '0': translations.ONLINK_QUICK_TODO.toLowerCase()
                                }
                            ),
                            type: TOAST_TYPE.ERROR
                        });
                    }
                }
            },
            onCancel: closeConfirmation
        });
    }

    React.useEffect(() => fetchEffectData(fetchTodoTemplates), []);

    return (
        <div className='settings-container data-grid'>
            <DataGridWrapper
                columns={getDataGridColumns(
                    translations,
                    fetchTodoTemplates,
                    openQuickTodoDialog
                )}
                deleteHandler={deleteTodoTemplateById}
                filterComponent={getFilterComponent(
                    translations,
                    openQuickTodoDialog,
                    fetchTodoTemplates
                )}
                getRowId={(row) => row.todoTemplateId}
                initialState={{
                    sorting: {
                        sortModel: [{
                            field: 'title',
                            sort: 'asc'
                        }]
                    }
                }}
                loading={loading}
                rows={todoTemplates}
                searchFields={['title']}
                searchable={true}
                showDeleteColumn={true}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

QuickTodoSettings.propTypes = {
    closeConfirmation: PropTypes.func,
    openConfirmation: PropTypes.func,
    openQuickTodoDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openQuickTodoDialog(props) {
            dispatch(openDialog(dialogTypes.QUICK_TODO_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(QuickTodoSettings);
